import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Button, Snackbar, Stack, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import {
  deleteNpcEntryAction,
  getNpcEntries,
  getNpcTeamEntriesEditable,
  resetAddNpcEntryAction,
  resetDeleteNpcEntryAction,
  resetEditNpcEntryAction,
} from 'src/redux/actions/npcLogActions';
import NpcLogTable from './npc-log-table';
import EditNpcLog from './edit-npc-log';
import DeleteConfirmationModal from './delete-confirmation-modal';
import { usePermission } from 'src/rbac';

// Therapist's own NPC entries (THIN-7740). Self-scoped: every row is the caller's own.
const NpcMyEntries = () => {
  const dispatch = useDispatch();
  const canViewNpc = usePermission('VIEW_TEAM_NPC');

  const plainList = useSelector((s: any) => s.getNpcEntriesReducer);
  const editableList = useSelector((s: any) => s.getNpcTeamEntriesEditableReducer);
  const list = canViewNpc ? editableList : plainList;

  const addRes = useSelector((s: any) => s.addNpcEntryReducer);
  const editRes = useSelector((s: any) => s.editNpcEntryReducer);
  const deleteRes = useSelector((s: any) => s.deleteNpcEntryReducer);

  const [range, setRange] = useState(() => {
    const today = new Date();
    return {
      from: format(startOfWeek(today, { weekStartsOn: 0 }), 'yyyy-MM-dd'),
      to: format(endOfWeek(today, { weekStartsOn: 0 }), 'yyyy-MM-dd'),
    };
  });
  const [modal, setModal] = useState<any>({ open: false, mode: 'ADD', entry: null });
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [toast, setToast] = useState<any>({ open: false, msg: '', severity: 'success' });

  const closeModal = () => setModal({ open: false, mode: 'ADD', entry: null });
  const refresh = () =>
    dispatch(
      canViewNpc
        ? getNpcTeamEntriesEditable({ from: range.from, to: range.to })
        : getNpcEntries({ from: range.from, to: range.to })
    );
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, canViewNpc]);

  const settle = (res: any, resetAction: any, okMsg: string, onOk?: () => void) => {
    const st = res?.response?.status;
    if (st === 200 || st === 201) {
      setToast({ open: true, msg: okMsg, severity: 'success' });
      onOk && onOk();
      refresh();
      dispatch(resetAction());
    } else if (res?.response && st) {
      setToast({
        open: true,
        msg: res?.response?.response?.message || 'Something went wrong. Please try again.',
        severity: 'error',
      });
      dispatch(resetAction());
    }
  };

  useEffect(() => {
    settle(addRes, resetAddNpcEntryAction, 'NPC entry added.', closeModal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addRes]);
  useEffect(() => {
    settle(editRes, resetEditNpcEntryAction, 'NPC entry updated.', closeModal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editRes]);
  useEffect(() => {
    settle(deleteRes, resetDeleteNpcEntryAction, 'NPC entry deleted.', () => setDeleteTarget(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteRes]);

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Stack
          direction="row"
          spacing={2}
        >
          <TextField
            label="From"
            type="date"
            size="small"
            value={range.from}
            onChange={(e) => setRange((r: any) => ({ ...r, from: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={range.to}
            onChange={(e) => setRange((r: any) => ({ ...r, to: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setModal({ open: true, mode: 'ADD', entry: null })}
        >
          Add Entry
        </Button>
      </Stack>

      <NpcLogTable
        list={list}
        onEdit={(e: any) => setModal({ open: true, mode: 'EDIT', entry: e })}
        onDelete={(e: any) => setDeleteTarget(e)}
      />

      <EditNpcLog
        open={modal.open}
        mode={modal.mode}
        entry={modal.entry}
        onClose={closeModal}
      />

      <DeleteConfirmationModal
        open={!!deleteTarget}
        loading={deleteRes?.loading}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && dispatch(deleteNpcEntryAction(deleteTarget.uuid))}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t: any) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((t: any) => ({ ...t, open: false }))}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NpcMyEntries;
